import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
  AdminPermissions,
  AdminRole,
  AdminDepartment,
  normalizeDepartment,
  normalizeRole,
  resolvePermissions,
} from '../utils/adminPermissions';

const AuthLoadingScreen = () => (
  <Box
    sx={{
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2.5,
      backgroundColor: '#0F0F10',
      fontFamily: "'Outfit', sans-serif",
    }}
  >
    <CircularProgress size={44} thickness={4} sx={{ color: '#7B1FA2' }} />
    <Typography
      sx={{
        color: '#FFFFFF',
        fontFamily: "'Outfit', sans-serif",
        fontWeight: 600,
        fontSize: 18,
        letterSpacing: 0.4,
      }}
    >
      Nouvak Admin
    </Typography>
    <Typography
      sx={{
        color: '#8A8D91',
        fontFamily: "'Outfit', sans-serif",
        fontSize: 14,
      }}
    >
      Preparing your workspace…
    </Typography>
  </Box>
);

interface AuthState {
  token: string | null;
  userName: string | null;
  userType: string | null;
  userId: string | null;
  role: AdminRole | null;
  department: AdminDepartment | null;
  permissions: AdminPermissions | null;
  useCustomPermissions: boolean;
  customPermissions: { modules: AdminPermissions['modules'] } | null;
  isLoggedIn: boolean;
}

interface Dimensions {
  header: { width: number; height: number };
  sidebar: { width: number; height: number };
}

interface LoginPayload {
  token: string;
  userName: string;
  userId: string;
  role: AdminRole;
  department?: AdminDepartment | null;
  permissions?: AdminPermissions | null;
  useCustomPermissions?: boolean;
  userType?: string | null;
}

interface AuthContextType {
  authState: AuthState;
  login: (payload: LoginPayload) => boolean;
  logout: () => boolean;
  verify: () => { isAuthentic: boolean };
  dimensions: Dimensions;
  setDimensions: React.Dispatch<React.SetStateAction<Dimensions>>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const hydrateAuthState = (parsed: Record<string, unknown>): AuthState => {
  const role = normalizeRole(
    parsed.role as string | undefined,
    parsed.userType as string | number | undefined,
  );
  const department = normalizeDepartment(parsed.department as string | undefined);

  const storedPermissions = parsed.permissions as AdminPermissions | undefined;
  const storedCustomPermissions = parsed.customPermissions as
    | { modules: AdminPermissions['modules'] }
    | undefined;
  const customModules =
    storedCustomPermissions?.modules || storedPermissions?.modules;

  const permissions = parsed.useCustomPermissions && customModules
    ? resolvePermissions(role, department, {
        useCustomPermissions: true,
        customPermissions: { modules: customModules },
      })
    : resolvePermissions(role, department, storedPermissions);

  return {
    token: (parsed.token as string) || null,
    userName: (parsed.userName as string) || null,
    userType: (parsed.userType as string) || role,
    userId: (parsed.userId as string) || null,
    role,
    department,
    permissions,
    useCustomPermissions: Boolean(parsed.useCustomPermissions),
    customPermissions:
      (parsed.customPermissions as AuthState['customPermissions']) || null,
    isLoggedIn: Boolean(parsed.token),
  };
};

const emptyAuthState: AuthState = {
  token: null,
  userName: null,
  userType: null,
  userId: null,
  role: null,
  department: null,
  permissions: null,
  useCustomPermissions: false,
  customPermissions: null,
  isLoggedIn: false,
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [authState, setAuthState] = useState<AuthState>(emptyAuthState);

  const [dimensions, setDimensions] = useState<Dimensions>({
    header: { width: 0, height: 0 },
    sidebar: { width: 0, height: 0 },
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const storedData = localStorage.getItem('authData');
      if (storedData) {
        const parsed = JSON.parse(storedData);
        setAuthState(hydrateAuthState(parsed));
      }
    } catch {
      // Corrupt/legacy auth data — clear it and fall back to logged-out
      // rather than crashing the app to a raw error screen on reload.
      localStorage.removeItem('authData');
      setAuthState(emptyAuthState);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = (payload: LoginPayload) => {
    try {
      const role = normalizeRole(payload.role, payload.userType);
      const department = normalizeDepartment(payload.department);
      const permissions = resolvePermissions(
        role,
        department,
        payload.permissions?.useCustomPermissions
          ? payload.permissions
          : payload.useCustomPermissions
            ? {
                useCustomPermissions: true,
                customPermissions: payload.permissions?.modules
                  ? { modules: payload.permissions.modules }
                  : undefined,
              }
            : payload.permissions,
      );
      const authData = {
        token: payload.token,
        userName: payload.userName,
        userId: payload.userId,
        userType: payload.userType || role,
        role,
        department,
        permissions,
        useCustomPermissions: Boolean(
          payload.useCustomPermissions || permissions.useCustomPermissions,
        ),
        customPermissions: permissions.useCustomPermissions
          ? { modules: permissions.modules }
          : null,
      };
      localStorage.setItem('authData', JSON.stringify(authData));
      setAuthState({ ...authData, isLoggedIn: true });
      return true;
    } catch {
      throw new Error('##Error While Login AuthContext.tsx');
    }
  };

  const logout = () => {
    try {
      localStorage.removeItem('authData');
      setAuthState(emptyAuthState);
      return true;
    } catch {
      throw new Error('##Error While Logout AuthContext.tsx');
    }
  };

  const verify = (): { isAuthentic: boolean } => {
    return { isAuthentic: !!authState.token };
  };

  return (
    <AuthContext.Provider
      value={{ authState, login, logout, verify, setDimensions, dimensions }}
    >
      {loading ? <AuthLoadingScreen /> : children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
